// ===========================================
// Integrations Overview Tab
// Dashboard showing status of all integrations
// ===========================================

import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  AccountBalance as XeroIcon,
  Folder as DriveIcon,
  Sms as SmsIcon,
  WhatsApp as WhatsAppIcon,
  Payment as PaymentIcon,
  Email as EmailIcon,
  Tag as SlackIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useIntegrationOverview } from '../../../hooks/useIntegrations';

// ===========================================
// TYPES
// ===========================================

interface IntegrationCardProps {
  title: string;
  icon: React.ReactNode;
  enabled: boolean;
  isConnected?: boolean;
  isConfigured?: boolean;
  stats?: React.ReactNode;
  lastSync?: string | null;
  errorCount?: number;
}

// ===========================================
// INTEGRATION CARD COMPONENT
// ===========================================

function IntegrationCard({
  title,
  icon,
  enabled,
  isConnected,
  isConfigured,
  stats,
  lastSync,
  errorCount,
}: IntegrationCardProps) {
  const getStatusChip = () => {
    if (!enabled) {
      return <Chip icon={<CloseIcon />} label="Disabled" size="small" color="default" />;
    }
    if (isConnected === false || isConfigured === false) {
      return <Chip icon={<WarningIcon />} label="Not Connected" size="small" color="warning" />;
    }
    if (errorCount && errorCount > 0) {
      return <Chip icon={<WarningIcon />} label={`${errorCount} Errors`} size="small" color="error" />;
    }
    return <Chip icon={<CheckIcon />} label="Active" size="small" color="success" />;
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ color: enabled ? 'primary.main' : 'text.disabled' }}>{icon}</Box>
            <Typography variant="h6" component="div">
              {title}
            </Typography>
          </Stack>
          {getStatusChip()}
        </Stack>

        {stats && <Box sx={{ mb: 2 }}>{stats}</Box>}

        {lastSync && (
          <Typography variant="caption" color="text.secondary">
            Last sync: {new Date(lastSync).toLocaleString()}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function IntegrationsOverviewTab() {
  const { data: overview, isLoading, error } = useIntegrationOverview();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load integration overview. Please try again.
      </Alert>
    );
  }

  if (!overview) {
    return null;
  }

  const { googleCalendar, xero, googleDrive, twilio, whatsapp, stripe, sendGrid, slack } = overview;

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        View the status of all third-party integrations. Click on each tab to configure individual
        integrations.
      </Typography>

      <Grid container spacing={3}>
        {/* Google Calendar */}
        <Grid item xs={12} md={6}>
          <IntegrationCard
            title="Google Calendar"
            icon={<CalendarIcon />}
            enabled={googleCalendar.enabled}
            isConnected={googleCalendar.connectedUsers > 0}
            errorCount={googleCalendar.errorCount}
            lastSync={googleCalendar.lastSyncAt}
            stats={
              <Stack spacing={1}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Connected Users
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={
                        googleCalendar.totalEligibleUsers > 0
                          ? (googleCalendar.connectedUsers / googleCalendar.totalEligibleUsers) * 100
                          : 0
                      }
                      sx={{ flexGrow: 1 }}
                    />
                    <Typography variant="body2">
                      {googleCalendar.connectedUsers}/{googleCalendar.totalEligibleUsers}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Allowed roles: {googleCalendar.allowedRoles.join(', ')}
                </Typography>
              </Stack>
            }
          />
        </Grid>

        {/* Xero (the server sends null without operations_invoices.read) */}
        {xero && (
        <Grid item xs={12} md={6}>
          <IntegrationCard
            title="Xero Accounting"
            icon={<XeroIcon />}
            enabled={xero.enabled}
            isConnected={xero.isConnected}
            errorCount={xero.errorCount}
            lastSync={xero.lastSyncAt}
            stats={
              xero.isConnected ? (
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Organization: {xero.tenantName || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    {xero.syncedInvoices} invoices synced | {xero.syncedContacts} contacts synced
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Connect your Xero account to sync invoices and payments
                </Typography>
              )
            }
          />
        </Grid>
        )}

        {/* Google Drive */}
        <Grid item xs={12} md={6}>
          <IntegrationCard
            title="Google Drive"
            icon={<DriveIcon />}
            enabled={googleDrive.enabled}
            isConnected={googleDrive.isConnected}
            lastSync={googleDrive.lastSyncAt}
            stats={
              googleDrive.isConnected ? (
                <Typography variant="body2">
                  {googleDrive.linkedFolders} folders linked | {googleDrive.totalFiles} files
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Connect Google Drive to share files with students and parents
                </Typography>
              )
            }
          />
        </Grid>

        {/* Twilio SMS */}
        <Grid item xs={12} md={6}>
          <IntegrationCard
            title="SMS (Twilio)"
            icon={<SmsIcon />}
            enabled={twilio.enabled}
            isConfigured={twilio.isConfigured}
            stats={
              twilio.isConfigured ? (
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    Today: {twilio.messagesSentToday} | This month: {twilio.messagesSentThisMonth}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Delivery rate: {twilio.deliverySuccessRate}%
                  </Typography>
                  {twilio.smsEnabled && (
                    <Chip label="SMS Active" size="small" color="primary" variant="outlined" />
                  )}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Configure Twilio to send SMS notifications
                </Typography>
              )
            }
          />
        </Grid>

        {/* WhatsApp (Meta Cloud API) */}
        <Grid item xs={12} md={6}>
          <IntegrationCard
            title="WhatsApp"
            icon={<WhatsAppIcon />}
            enabled={whatsapp.enabled}
            isConfigured={whatsapp.isConfigured}
            stats={
              whatsapp.isConfigured ? (
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    Today: {whatsapp.messagesSentToday} | This month: {whatsapp.messagesSentThisMonth}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Delivery rate: {whatsapp.deliverySuccessRate}%
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Configure WhatsApp Business API for messaging
                </Typography>
              )
            }
          />
        </Grid>

        {/* Stripe */}
        <Grid item xs={12} md={6}>
          <IntegrationCard
            title="Stripe Payments"
            icon={<PaymentIcon />}
            enabled={stripe.enabled}
            isConfigured={stripe.isConfigured}
            stats={
              stripe.isConfigured ? (
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="success.main">
                    Payment processing active
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Chip
                      label={stripe.webhookConfigured ? 'Webhooks OK' : 'Webhooks Not Configured'}
                      size="small"
                      color={stripe.webhookConfigured ? 'success' : 'warning'}
                      variant="outlined"
                    />
                  </Stack>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Configure Stripe to accept online payments
                </Typography>
              )
            }
          />
        </Grid>

        {/* Email Delivery */}
        <Grid item xs={12} md={6}>
          <IntegrationCard
            title="Email Delivery"
            icon={<EmailIcon />}
            enabled={sendGrid.enabled}
            isConfigured={sendGrid.isConfigured}
            stats={
              sendGrid.isConfigured ? (
                <Typography variant="body2" color="success.main">
                  Email delivery active
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Email delivery is managed by the platform
                </Typography>
              )
            }
          />
        </Grid>

        {/* Slack */}
        <Grid item xs={12} md={6}>
          <IntegrationCard
            title="Slack"
            icon={<SlackIcon />}
            enabled={slack?.enabled || false}
            isConnected={slack?.isConnected || false}
            stats={
              slack?.isConnected ? (
                <Stack spacing={0.5}>
                  <Typography variant="body2">
                    Workspace: {slack.teamName || 'Connected'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {slack.linkedUsersCount} linked user{slack.linkedUsersCount !== 1 ? 's' : ''}
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Connect Slack to receive notifications in channels
                </Typography>
              )
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
}
